<script setup lang="ts">
import { computed, ref, watch } from "vue";
import type { ComponentExposed } from "vue-component-type-helpers";

import DropdownMenu from "~/components/dumb/DropdownMenu.vue";
import IconOrEmojiPicker from "~/components/dumb/IconOrEmojiPicker.vue";
import PageIcon from "~/components/dumb/PageIcon.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import { copyAndNotify } from "~/components/notifications";
import { ChangeIconIcon, DuidFieldIcon, RenameIcon, TrashIcon } from "~/icons";
import { DropdownMenuItemKind, EmojiSkinTone, IconKind } from "~/shared/enums";
import type { TaskKind } from "~/shared/types";
import { useDataStore, useUserStore } from "~/stores";
import { getEmojiRecommendation } from "~/utils/recommendation";

import SettingsTableItem from "./SettingsTableItem.vue";

const DELETE_TYPE_DIALOG_DESCRIPTION =
  "Permanently deleting a type will move all its tasks to the default type. This can't be undone. Are you sure you want to proceed?";

const props = defineProps<{
  taskKind: TaskKind;
  isFirst?: boolean;
}>();

const dataStore = useDataStore();
const userStore = useUserStore();

const iconPicker = ref<InstanceType<typeof DropdownMenu> | null>(null);
const tableItemRef = ref<ComponentExposed<typeof SettingsTableItem> | null>(null);

const iconKind = ref(props.taskKind.iconKind);
const iconNameOrEmoji = ref(props.taskKind.iconNameOrEmoji);
const colorHex = ref(props.taskKind.colorHex);

watch(
  () => props.taskKind,
  (newKind) => {
    iconKind.value = newKind.iconKind;
    iconNameOrEmoji.value = newKind.iconNameOrEmoji;
    colorHex.value = newKind.colorHex;
  }
);

const titleExists = (title: string) => {
  const normTitle = title.trim().toLowerCase();
  return dataStore.taskKindList.some(
    (kind) => kind.title.toLowerCase() === normTitle && kind.duid !== props.taskKind.duid
  );
};

const onSave = async (newTitle: string) => {
  // Get icon recommendation if this is a new type (iconKind is NONE)
  let emojiUpdate: { iconKind: IconKind.EMOJI; iconNameOrEmoji: string } | undefined;
  if (props.taskKind.iconKind === IconKind.NONE) {
    emojiUpdate = await getEmojiRecommendation(props.taskKind.duid, newTitle);
  }

  await dataStore.updateTaskKind({
    duid: props.taskKind.duid,
    title: newTitle,
    iconKind: emojiUpdate?.iconKind ?? iconKind.value,
    iconNameOrEmoji: emojiUpdate?.iconNameOrEmoji ?? iconNameOrEmoji.value,
    colorHex: colorHex.value,
  });

  if (emojiUpdate) {
    iconKind.value = emojiUpdate.iconKind;
    iconNameOrEmoji.value = emojiUpdate.iconNameOrEmoji;
  }
};

const onDelete = () => {
  dataStore.deleteTaskKind(props.taskKind);
};

const startEditing = () => {
  tableItemRef.value?.startEditing();
};

const openIconPicker = () => {
  iconPicker.value?.open();
};

const iconPickerSections = computed(() => [
  {
    title: "Modify",
    items: [
      {
        title: "Icons",
        kind: DropdownMenuItemKind.COMPONENT,
        noFocus: true,
        component: IconOrEmojiPicker,
        componentArgs: {
          iconKind: iconKind.value,
          iconNameOrEmoji: iconNameOrEmoji.value,
          colorHex: colorHex.value,
          onSelectColor: (newColorHex: string) => {
            colorHex.value = newColorHex;
            dataStore.updateTaskKind({ duid: props.taskKind.duid, colorHex: newColorHex });
          },
          onSelectSkinTone: (skinTone: EmojiSkinTone) => {
            userStore.setSkinTone(skinTone);
          },
          onSelectIconOrEmoji: (newIconKind: IconKind, newIconNameOrEmoji: string) => {
            iconKind.value = newIconKind;
            iconNameOrEmoji.value = newIconNameOrEmoji;
            dataStore.updateTaskKind({
              duid: props.taskKind.duid,
              iconKind: newIconKind,
              iconNameOrEmoji: newIconNameOrEmoji,
            });
          },
          onAfterSelect: () => {
            iconPicker.value?.close();
          },
        },
      },
    ],
  },
]);

const dropdownSections = computed(() => [
  {
    title: "Type",
    items: [
      {
        title: "Rename",
        kind: DropdownMenuItemKind.BUTTON,
        icon: RenameIcon,
        onClick: startEditing,
      },
      {
        title: "Change icon",
        kind: DropdownMenuItemKind.BUTTON,
        icon: ChangeIconIcon,
        onClick: openIconPicker,
      },
      {
        title: "Copy ID",
        kind: DropdownMenuItemKind.BUTTON,
        icon: DuidFieldIcon,
        onClick: () => copyAndNotify("Type ID", props.taskKind.duid),
      },
      {
        title: "Delete",
        kind: DropdownMenuItemKind.BUTTON,
        icon: TrashIcon,
        disabled: props.taskKind.locked,
        disabledReason: props.taskKind.locked ? "This is the default type" : undefined,
        onClick: () => tableItemRef.value?.openDeleteDialog(),
      },
    ],
  },
]);

const iconConfig = computed(() => ({
  iconKind: iconKind.value,
  iconNameOrEmoji: iconNameOrEmoji.value,
  colorHex: colorHex.value,
}));

defineExpose({
  duid: computed(() => props.taskKind.duid),
  startEditing,
});
</script>

<template>
  <SettingsTableItem
    ref="tableItemRef"
    :item="taskKind"
    :is-first="isFirst"
    :delete-dialog-title="`Delete ${taskKind.title}`"
    :delete-dialog-description="DELETE_TYPE_DIALOG_DESCRIPTION"
    :dropdown-sections="dropdownSections"
    locked-tooltip="This is the default type and can't be deleted"
    :title-exists="titleExists"
    :on-save="onSave"
    :on-delete="onDelete">
    <template #icon>
      <DropdownMenu ref="iconPicker" :sections="iconPickerSections" :distance="0" :width-pixels="383">
        <Tooltip text="Change icon">
          <div class="mx-1 flex shrink-0 cursor-pointer items-center justify-center rounded-sm p-1 hover:bg-md">
            <PageIcon :page="iconConfig" class="icon-md" />
          </div>
        </Tooltip>
      </DropdownMenu>
    </template>
  </SettingsTableItem>
</template>
